import { trackClickTariffSelect } from "@/lib/metrics";
import { siteConfig } from "@/config/site";
import { landingContent } from "@/content/landing";
import { buildAppUrl, featureGuestTranscription } from "@/lib/config";
import { appendUtmToUrl } from "@/lib/utm-forward";
import { scrollToAnchor } from "@/lib/scroll-to-anchor";
import { Button } from "./Button";
import { buildPlanPriceView } from "@/lib/plan-price-view";
import { type PaddlePriceBreakdown } from "@/lib/paddle-pricing";

function LightningIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="shrink-0 text-[var(--brand-primary)]">
      <path d="M13 2L4.5 13.5H11l-1 8.5L19.5 10H13l0-8z" />
    </svg>
  );
}

interface PricingCardProps {
  className?: string;
  title: string;
  description: string;
  ctaLabel: string;
  featured?: boolean;
  badge?: string;
  planId: string;
  isYear?: boolean;
  onCtaClick?: () => void;
  priceBreakdown?: PaddlePriceBreakdown | null;
  monthlyBreakdown?: PaddlePriceBreakdown | null;
  extraMinuteBreakdown?: PaddlePriceBreakdown | null;
}

export function PricingCard({
  className = "", title, description, ctaLabel,
  featured = false, badge, planId, isYear, onCtaClick,
  priceBreakdown, monthlyBreakdown, extraMinuteBreakdown,
}: PricingCardProps) {

  const pricing = landingContent.pricing;
  const isComingSoon = siteConfig.ctaMode === "coming-soon";
  const { isFree, price, oldPrice, yearTotal, minutes, overage } = buildPlanPriceView({
    planId,
    isYear,
    priceBreakdown,
    monthlyBreakdown,
    extraMinuteBreakdown,
  });

  const showYearBadge = isYear && !isFree;

  return (
    <div className={`relative bg-white border border-solid flex flex-col rounded-[24px] w-full hover:shadow-[0px_0px_10px_rgba(0,0,0,0.07)] transition-shadow ${featured ? "border-[1.5px] border-[#FF7900]" : "border-[var(--brand-primary-soft)]"} ${className}`} style={{ padding: "30px 26px" }}>
      {featured && (
        <div className="absolute inset-0 rounded-[24px] pointer-events-none overflow-hidden">
          <img alt="" src="/assets/plan-popular-bg.png" className="absolute inset-0 size-full object-cover opacity-10 rounded-[24px]" />
        </div>
      )}

      {badge && (
        <div className="absolute left-1/2 -translate-x-1/2 top-[-13px] bg-[#FF7900] flex items-center justify-center px-[16px] py-[5px] rounded-[16px] z-10">
          <span className="[font-family:var(--font-commissioner)] font-medium not-italic text-[15px] leading-[20px] text-white whitespace-nowrap" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>{badge}</span>
        </div>
      )}

      {showYearBadge && (
        <div className="absolute top-[18px] right-[18px] bg-[#FFF3E6] flex items-center justify-center px-[10px] py-[3px] rounded-[999px] z-[2]">
          <span className="[font-family:var(--font-commissioner)] font-semibold not-italic text-[13px] leading-[16px] text-[#FF7900] whitespace-nowrap" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>−50%</span>
        </div>
      )}

      <div className="relative flex flex-col z-[1]" style={{ gap: 4, marginBottom: 22 }}>
        <h3 className="[font-family:var(--font-commissioner)] font-bold not-italic text-[20px] leading-[24px] text-[#1A1D1F]" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>{title}</h3>
        <p className="[font-family:var(--font-commissioner)] font-normal not-italic text-[14px] leading-[18px] text-[#94A3B8]" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>{description}</p>
      </div>

      <div className="relative flex flex-col z-[1]" style={{ marginBottom: 22, minHeight: 120 }}>
        <div className="flex items-baseline" style={{ gap: 7 }}>
          <span className="[font-family:var(--font-commissioner)] font-bold not-italic text-[42px] leading-[48px] text-[#1A1D1F] tracking-[-0.02em]" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>{price}</span>
          <span className="[font-family:var(--font-commissioner)] font-normal not-italic text-[15px] leading-[20px] text-[#64748B]" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>{pricing.perMonth}</span>
        </div>

        <div style={{ minHeight: 20, marginTop: 4 }}>
          {oldPrice && (
            <span className="[font-family:var(--font-commissioner)] font-normal not-italic text-[15px] leading-[20px] text-[#94A3B8] line-through" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>{oldPrice} {pricing.perMonth}</span>
          )}
        </div>

        <div style={{ marginTop: 8 }}>
          {isYear ? (
            <span className="[font-family:var(--font-commissioner)] font-normal not-italic text-[14px] leading-[18px] text-[#64748B]" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>
              {pricing.annualBilling}{yearTotal ? <> — <b className="font-bold">{yearTotal}</b></> : ""}
            </span>
          ) : !isFree ? (
            <span className="[font-family:var(--font-commissioner)] font-normal not-italic text-[14px] leading-[18px] text-[#64748B]" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>{pricing.monthlyBilling}</span>
          ) : null}
        </div>

      </div>

      <div className="w-full shrink-0" style={{ height: 1, background: "#E7ECF3", marginBottom: 22 }} />

      {minutes && (
        <div className="relative flex items-center z-[1]" style={{ gap: 9, marginBottom: 7 }}>
          <LightningIcon />
          <span className="[font-family:var(--font-commissioner)] font-semibold not-italic text-[16px] leading-[20px] text-[#1A1D1F]" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>{minutes}</span>
        </div>
      )}

      {overage && (
        <p className="[font-family:var(--font-commissioner)] font-normal not-italic text-[13px] leading-[16px] text-[#64748B] z-[1]" style={{ marginLeft: 27, marginBottom: 0, fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>
          {overage}
        </p>
      )}

      <div className="relative z-[1] w-full" style={{ marginTop: "auto", paddingTop: 24 }}>
        <Button
          variant={featured ? "accent" : "primary"}
          className="w-full"
          style={{ padding: "11px 12px", borderRadius: 14, fontWeight: 600, fontSize: 16 }}
          onClick={() => {
            trackClickTariffSelect(title);
            onCtaClick?.();
            if (isComingSoon) {
              scrollToAnchor("#pricing");
              return;
            }
            // Период, выбранный здесь, едет в кабинет: иначе чекаут откроется
            // на периоде по умолчанию, а не на том, что человек выбрал.
            const period = isYear ? "year" : "month";
            const target = featureGuestTranscription
              ? buildAppUrl(`/subscription?checkout=${encodeURIComponent(planId)}&period=${period}`)
              : buildAppUrl(`/login?source=tariff&plan=${encodeURIComponent(planId)}&period=${period}`);
            window.open(appendUtmToUrl(target), "_self");
          }}
        >
          <span>{ctaLabel}</span>
        </Button>
      </div>
    </div>
  );
}
